// ai-print.js source — this is the readable file. Edit THIS, then obfuscate
// it (e.g. `npx javascript-obfuscator ai-print.source.js --output ai-print.js`
// with matching options) to produce the deployed ai-print.js Shopify asset.
// Kept in lockstep with main.html's inline <script> (same widget, same IDs) —
// main.html is the local/dev test harness for this exact logic.

// ── CONFIG ─────────────────────────────────────────────────────
const AW_SUPABASE_URL  = 'https://jwiuxklfllcsqpooabpt.supabase.co'
const AW_SHOPIFY_STORE = 'oceansilhouettes.com'
const AW_ANON_KEY      = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp3aXV4a2xmbGxjc3Fwb29hYnB0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODA5OTY5MTQsImV4cCI6MjA5NjU3MjkxNH0.I3-l_rlsNLwLje56qu_qS5u0KA0hLgJ1VFhc53tmLbA'

const awFn   = (n) => `${AW_SUPABASE_URL}/functions/v1/${n}`
const awAuth = { 'Authorization': `Bearer ${AW_ANON_KEY}`, 'apikey': AW_ANON_KEY }

// ── TURNSTILE ──────────────────────────────────────────────────
// Rendered explicitly from here instead of Turnstile's class-based auto-render.
// With auto-render, api.js (async) could run before this deferred file had
// defined the callbacks it looks up by name; loading api.js from here means
// they always exist first. The Generate button stays locked until a token is
// in hand (see awUpdateBtn), so no request ever goes out without one.
const AW_TURNSTILE_SITEKEY    = '0x4AAAAAAD5sMjbutL7oMtks'
const AW_TURNSTILE_SCRIPT     = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=awTurnstileOnload'
const AW_TURNSTILE_TIMEOUT_MS = 20000
const AW_TURNSTILE_SCRIPT_RETRY_MS = [1500, 4000] // waits before the 2nd and 3rd api.js download
const AW_IS_IN_APP_BROWSER    = /FBAN|FBAV|FB_IAB|Instagram/i.test(navigator.userAgent)

let awTurnstileToken = null, awTurnstileWidgetId = null
let awTurnstileFailed = false, awTurnstileTimer = null, awTurnstileStartedAt = 0
let awTurnstileOkReported = false, awTurnstileTimeoutReported = false, awTurnstileErrorReports = 0
let awTurnstileLoadBegun = false

// ai-tracker.js listens for these; nothing here depends on it being loaded.
function awTurnstileReport(status, detail) {
  try { document.dispatchEvent(new CustomEvent('aw:turnstile', { detail: { status, ...detail } })) } catch { /* ignore */ }
}
// The widget's own steps, so the stats count what visitors actually did:
// photo_picked, photo_rejected (reason), generate_click.
function awTrackAction(action, detail) {
  try { document.dispatchEvent(new CustomEvent('aw:action', { detail: { action, ...detail } })) } catch { /* ignore */ }
}

function awTurnstileCallback(token) {
  awTurnstileToken = token
  awTurnstileFailed = false
  clearTimeout(awTurnstileTimer)
  awShowTurnstileHelp(false)
  if (!awTurnstileOkReported) {
    awTurnstileOkReported = true
    awTurnstileReport('ok', { ms: Date.now() - awTurnstileStartedAt })
  }
  awUpdateBtn()
  if (awAutoRetry) { awAutoRetry = false; awHandleCTA() }
}

// Turnstile keeps retrying on its own (retry: 'auto'); this just tells the
// visitor and offers a manual retry in case it never recovers.
function awTurnstileError(code) {
  awTurnstileToken = null
  awTurnstileFailed = true
  clearTimeout(awTurnstileTimer)
  if (awTurnstileErrorReports++ < 3) awTurnstileReport('error', { code: String(code || 'unknown') })
  awShowTurnstileHelp(true)
  awUpdateBtn()
}

function awTurnstileTokenLost() {
  awTurnstileToken = null
  awUpdateBtn()
}

// Only runs while the upload view is on screen (see awShowView), so a widget
// quietly re-solving behind the loading/email views never counts as a timeout.
function awStartTurnstileWatchdog() {
  clearTimeout(awTurnstileTimer)
  if (awTurnstileToken) return
  awTurnstileStartedAt = Date.now()
  awArmTurnstileWatchdog()
}

function awArmTurnstileWatchdog() {
  awTurnstileTimer = setTimeout(() => {
    if (awTurnstileToken) return
    // A hidden tab barely runs anything, Turnstile included — only time out on screen.
    if (document.visibilityState === 'hidden') { awArmTurnstileWatchdog(); return }
    awTurnstileFailed = true
    if (!awTurnstileTimeoutReported) {
      awTurnstileTimeoutReported = true
      awTurnstileReport('timeout', { ms: Date.now() - awTurnstileStartedAt })
    }
    awShowTurnstileHelp(true)
    awUpdateBtn()
  }, AW_TURNSTILE_TIMEOUT_MS)
}

function awShowTurnstileHelp(show) {
  const help = document.getElementById('awTurnstileHelp')
  if (!help) return
  help.classList.toggle('aw-hidden', !show)
  if (show) {
    document.getElementById('awTurnstileHelpText').textContent = AW_IS_IN_APP_BROWSER
      ? 'Verification is having trouble in the Facebook/Instagram browser. Try again, or tap ••• at the top and choose "Open in browser".'
      : 'Verification is taking longer than usual.'
  }
}

// Loads api.js, retrying a failed download twice (after 1.5s, then 4s) before
// giving up. Nothing loads, and no failure counts, while the page is hidden:
// in-app browsers (Facebook especially) load ad pages in the background and
// tear that load down, which surfaces as a bogus script error. Those wait
// until the page is actually on screen.
function awLoadTurnstile(attempt = 0) {
  awTurnstileLoadBegun = true
  if (document.visibilityState === 'hidden') {
    document.addEventListener('visibilitychange', function awOnVisible() {
      if (document.visibilityState === 'hidden') return
      document.removeEventListener('visibilitychange', awOnVisible)
      awLoadTurnstile(attempt)
    })
    return
  }
  if (attempt === 0) awStartTurnstileWatchdog()
  const script = document.createElement('script')
  script.src = AW_TURNSTILE_SCRIPT
  script.async = true
  script.onerror = () => {
    script.remove()
    if (window.turnstile) return
    if (document.visibilityState === 'hidden') { awLoadTurnstile(attempt); return }
    if (attempt < AW_TURNSTILE_SCRIPT_RETRY_MS.length) {
      setTimeout(() => awLoadTurnstile(attempt + 1), AW_TURNSTILE_SCRIPT_RETRY_MS[attempt])
      return
    }
    awTurnstileError('script_load_failed')
  }
  document.head.appendChild(script)
}

function awTurnstileOnload() {
  if (awTurnstileWidgetId !== null) return
  awTurnstileWidgetId = window.turnstile.render('#awTurnstile', {
    sitekey: AW_TURNSTILE_SITEKEY,
    theme: 'light',
    size: 'flexible',
    callback: awTurnstileCallback,
    'error-callback': awTurnstileError,
    'expired-callback': awTurnstileTokenLost,
    'timeout-callback': awTurnstileTokenLost,
  })
}

function awResetTurnstile() {
  awTurnstileToken = null
  if (window.turnstile && awTurnstileWidgetId !== null) window.turnstile.reset(awTurnstileWidgetId)
  awUpdateBtn()
}

// "Try again" link under the widget.
function awRetryTurnstile() {
  awTurnstileFailed = false
  awShowTurnstileHelp(false)
  if (window.turnstile && awTurnstileWidgetId !== null) {
    awResetTurnstile()
    awStartTurnstileWatchdog()
  } else {
    awLoadTurnstile() // api.js never loaded — fetch it again
  }
  awUpdateBtn()
}

// In Facebook/Instagram's in-app browser, Turnstile waits until the page has
// fully loaded (+500ms): its first check running while that browser is still
// busy loading looks like the source of the 600010 errors there. Visitors
// don't need a token until after picking a photo, so the wait costs nothing.
// Capped at 8s in case a slow image or pixel holds up the load event.
// Everyone else loads it straight away.
const AW_IN_APP_TURNSTILE_DELAY_MS = 500
const AW_IN_APP_TURNSTILE_MAX_WAIT_MS = 8000
function awStartTurnstile() {
  // Load event, the 8s cap or "Try again" — whichever comes first wins.
  if (awTurnstileLoadBegun) return
  awLoadTurnstile()
}
if (AW_IS_IN_APP_BROWSER) {
  const awDeferTurnstile = () => setTimeout(awStartTurnstile, AW_IN_APP_TURNSTILE_DELAY_MS)
  if (document.readyState === 'complete') awDeferTurnstile()
  else window.addEventListener('load', awDeferTurnstile, { once: true })
  setTimeout(awStartTurnstile, AW_IN_APP_TURNSTILE_MAX_WAIT_MS)
} else {
  awLoadTurnstile()
}

let awDisposableDomains = null
fetch(awFn('email-domains'), { headers: awAuth })
  .then(r => r.json())
  .then(d => { awDisposableDomains = new Set(d.domains || []) })
  .catch(() => { awDisposableDomains = new Set() })

function awIsDisposableEmail(email) {
  if (!awDisposableDomains) return false
  const domain = email.split('@')[1]?.toLowerCase()
  return domain ? awDisposableDomains.has(domain) : false
}

// These use position:fixed to cover the viewport, but a `transform` on any
// ancestor (common in Shopify themes — sticky headers, scroll animations,
// drawer wrappers) turns that ancestor into the fixed-positioning context
// instead of the viewport, pinning them to a corner rather than centering.
// Re-parenting straight onto <body> sidesteps whatever the theme does.
;['awLightbox', 'awToast', 'awCtxMenu'].forEach((id) => {
  const el = document.getElementById(id)
  if (el) document.body.appendChild(el)
})

// ── SLIDES ─────────────────────────────────────────────────────
const AW_SLIDE_COUNT = 4

const awDotsEl = document.getElementById('awDots')
for (let i = 0; i < AW_SLIDE_COUNT; i++) {
  const dot = document.createElement('button')
  dot.className = 'aw-dot' + (i === 0 ? ' active' : '')
  dot.onclick = () => awGoTo(i)
  awDotsEl.appendChild(dot)
}

let awCurrent = 0, awTimer = null

function awGoTo(n) {
  document.getElementById(`aw-slide-${awCurrent}`).classList.remove('active')
  document.querySelectorAll('.aw-dot')[awCurrent].classList.remove('active')
  awCurrent = n
  document.getElementById(`aw-slide-${awCurrent}`).classList.add('active')
  document.querySelectorAll('.aw-dot')[awCurrent].classList.add('active')
  clearInterval(awTimer)
  awTimer = setInterval(awNext, 3000)
}

function awNext() { awGoTo((awCurrent + 1) % AW_SLIDE_COUNT) }
awTimer = setInterval(awNext, 3000)

// ── DRAG AND DROP ──────────────────────────────────────────────
const awZone = document.getElementById('awUploadZone')
awZone.addEventListener('dragover', (e) => { e.preventDefault(); awZone.style.borderColor = 'var(--teal)'; awZone.style.background = 'var(--teal-light)' })
awZone.addEventListener('dragleave', () => { awZone.style.borderColor = ''; awZone.style.background = '' })
awZone.addEventListener('drop', (e) => {
  e.preventDefault()
  awZone.style.borderColor = ''; awZone.style.background = ''
  const file = e.dataTransfer.files[0]
  if (file) awProcessFile(file)
})

// ── STATE ──────────────────────────────────────────────────────
let awFile = null, awStyle = null, awJobId = null, awProductHandle = null, awBoatCount = 'single'
let awLastWatermarkedUrl = null, awAutoRetry = false

function awHandleFile(e) {
  const file = e.target.files[0]; if (!file) return
  awProcessFile(file)
}

// Shrinks the photo in-browser before it ever gets uploaded, so a 20+MP phone
// photo never has to be decoded at full resolution server-side (that's what
// was blowing the edge function's memory limit). TIFF can't be decoded via
// canvas in browsers, so it's left as-is — the server still handles it.
const AW_MAX_UPLOAD_DIMENSION = 2048
async function awResizeImageIfNeeded(file) {
  if (file.type === 'image/tiff' || /\.tiff?$/i.test(file.name)) return file
  try {
    const bitmap = await createImageBitmap(file)
    if (bitmap.width <= AW_MAX_UPLOAD_DIMENSION && bitmap.height <= AW_MAX_UPLOAD_DIMENSION) {
      bitmap.close()
      return file
    }
    const scale = AW_MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height)
    const targetW = Math.round(bitmap.width * scale)
    const targetH = Math.round(bitmap.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = targetW; canvas.height = targetH
    canvas.getContext('2d').drawImage(bitmap, 0, 0, targetW, targetH)
    bitmap.close()
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    if (!blob) return file
    return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
  } catch (e) {
    console.error('client-side resize failed, uploading original:', e)
    return file
  }
}

// Mirrors the server's own cap (supabase/functions/generate-preview) so an
// oversized file is rejected here, instantly, instead of only after the full
// Turnstile/PoW/upload round trip — where a payload this size can get dropped
// by the platform before it ever reaches our code's own error response.
const AW_MAX_UPLOAD_BYTES = 5 * 1024 * 1024
async function awProcessFile(file) {
  awTrackAction('photo_picked')
  const resized = await awResizeImageIfNeeded(file)
  if (resized.size > AW_MAX_UPLOAD_BYTES) {
    awTrackAction('photo_rejected', { reason: 'too_large' })
    awToast('Image too large (max 5 MB). Please choose a smaller photo.', true)
    document.getElementById('awFileInput').value = ''
    return
  }
  awFile = resized
  const reader = new FileReader()
  reader.onload = (ev) => { document.getElementById('awThumb').src = ev.target.result }
  reader.readAsDataURL(awFile)
  document.getElementById('awUploadZone').classList.add('aw-hidden')
  document.getElementById('awPreviewArea').classList.remove('aw-hidden')
  document.getElementById('awStylePicker').classList.remove('aw-hidden')
  document.getElementById('awCountPicker').classList.remove('aw-hidden')
  document.getElementById('awCarNameWrap').classList.remove('aw-hidden')
  // Only one style is visible right now — auto-select it so there's no pointless extra click.
  const firstStyleCard = document.querySelector('.aw-style-card')
  if (firstStyleCard) awSelectStyle(firstStyleCard)
  awUpdateBtn()
}

function awRemoveImage() {
  awFile = null; awStyle = null
  document.getElementById('awUploadZone').classList.remove('aw-hidden')
  document.getElementById('awPreviewArea').classList.add('aw-hidden')
  document.getElementById('awStylePicker').classList.add('aw-hidden')
  document.getElementById('awCountPicker').classList.add('aw-hidden')
  document.getElementById('awCarNameWrap').classList.add('aw-hidden')
  document.getElementById('awFileInput').value = ''
  document.getElementById('awThumb').src = ''
  document.querySelectorAll('.aw-style-card').forEach(c => c.classList.remove('selected'))
  awUpdateBtn()
}

function awSelectCount(value, btn) {
  awBoatCount = value
  document.querySelectorAll('.aw-count-btn').forEach(b => b.classList.remove('selected'))
  btn.classList.add('selected')
}

function awSelectStyle(card) {
  document.querySelectorAll('.aw-style-card').forEach(c => c.classList.remove('selected'))
  card.classList.add('selected')
  awStyle = card.dataset.style
  awUpdateBtn()
}

function awUpdateBtn() {
  const btn = document.getElementById('awCta')
  btn.disabled = false
  if (!awFile) {
    btn.textContent = 'See my free design →'; btn.className = 'aw-btn state-idle'
  } else if (!awStyle) {
    btn.textContent = 'Pick a style above ↑'; btn.className = 'aw-btn state-pick'
  } else if (!awTurnstileToken && awTurnstileFailed) {
    btn.textContent = 'Verification didn\'t load — try again above ↑'; btn.className = 'aw-btn state-pick'; btn.disabled = true
  } else if (!awTurnstileToken) {
    btn.innerHTML = '<span class="aw-btn-spinner"></span> Verifying you\'re human…'; btn.className = 'aw-btn state-solving'; btn.disabled = true
  } else {
    btn.textContent = 'See my free design →'; btn.className = 'aw-btn state-ready'
  }
}

// Proof-of-work: server hands out a signed challenge, this solves it by brute-forcing
// a nonce until SHA-256(id:expiresAt:difficulty:nonce) has `difficulty` leading zero bits.
// Runs in a Worker so the ~2-3s of hashing doesn't freeze the page.
const AW_POW_WORKER_SRC = `
  self.onmessage = async (e) => {
    const { id, difficulty, expiresAt } = e.data
    const enc = new TextEncoder()
    let nonce = 0
    while (true) {
      const data = enc.encode(id + ':' + expiresAt + ':' + difficulty + ':' + nonce)
      const hashBuf = await crypto.subtle.digest('SHA-256', data)
      if (awLeadingZeroBits(new Uint8Array(hashBuf)) >= difficulty) {
        self.postMessage({ nonce })
        return
      }
      nonce++
    }
  }
  function awLeadingZeroBits(bytes) {
    let count = 0
    for (const byte of bytes) {
      if (byte === 0) { count += 8; continue }
      for (let i = 7; i >= 0; i--) {
        if ((byte >> i) & 1) return count
        count++
      }
      return count
    }
    return count
  }
`
function awSolvePow(id, difficulty, expiresAt) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([AW_POW_WORKER_SRC], { type: 'application/javascript' })
    const worker = new Worker(URL.createObjectURL(blob))
    worker.onmessage = (e) => { resolve(e.data.nonce); worker.terminate() }
    worker.onerror = (e) => { reject(e); worker.terminate() }
    worker.postMessage({ id, difficulty, expiresAt })
  })
}

// Best-effort device signal for the secondary rate-limit key: canvas render quirks +
// screen res + core count + WebGL renderer/vendor. No timezone (too identity-adjacent).
// Any piece that throws or is unavailable is just skipped, never blocks generation.
async function awDeviceFingerprint() {
  const parts = []
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 220; canvas.height = 40
    const ctx = canvas.getContext('2d')
    ctx.textBaseline = 'top'
    ctx.font = '14px "Arial"'
    ctx.fillStyle = '#f60'
    ctx.fillRect(0, 0, 220, 40)
    ctx.fillStyle = '#069'
    ctx.fillText('AW-fp ⛵ boat-portrait 0123', 2, 2)
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.fillText('AW-fp ⛵ boat-portrait 0123', 4, 4)
    parts.push(canvas.toDataURL())
  } catch { parts.push('') }
  try { parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`) } catch { parts.push('') }
  try { parts.push(String(navigator.hardwareConcurrency || '')) } catch { parts.push('') }
  try {
    const glCanvas = document.createElement('canvas')
    const gl = glCanvas.getContext('webgl') || glCanvas.getContext('experimental-webgl')
    if (gl) {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info')
      if (dbg) {
        parts.push(String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)))
        parts.push(String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)))
      } else {
        parts.push(String(gl.getParameter(gl.VENDOR)))
        parts.push(String(gl.getParameter(gl.RENDERER)))
      }
    }
  } catch { /* ignore */ }
  try {
    const hashBuf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts.join('|')))
    return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return ''
  }
}

const AW_ALLOWED_HOSTS = new Set([AW_SHOPIFY_STORE, `www.${AW_SHOPIFY_STORE}`, `eu.${AW_SHOPIFY_STORE}`])
// Theme editor / preview links run on the *.myshopify.com handle, not the
// custom domain — allow that too, so testing there isn't silently blocked.
const AW_IS_MYSHOPIFY_PREVIEW = /\.myshopify\.com$/.test(location.hostname)
// Local dev: opening this file through a local server skips the live-store
// check entirely, so you don't have to push to Shopify to test changes.
const AW_IS_LOCAL_DEV = ['localhost', '127.0.0.1'].includes(location.hostname)

async function awHandleCTA() {
  console.log('[AW] awHandleCTA fired', { hasFile: !!awFile, awStyle, hostname: location.hostname, hasTurnstileToken: !!awTurnstileToken })
  if (!awFile || !awStyle) { console.log('[AW] blocked: missing file or style'); return }
  if (!AW_ALLOWED_HOSTS.has(location.hostname) && !AW_IS_MYSHOPIFY_PREVIEW && !AW_IS_LOCAL_DEV) {
    console.log('[AW] blocked: host check failed')
    awToast('This widget only works on the live store.', true)
    return
  }
  // The button is locked without a token; this only catches programmatic calls.
  if (!awTurnstileToken) {
    console.log('[AW] blocked: no turnstile token')
    awUpdateBtn()
    return
  }
  console.log('[AW] passed all gates, starting pow-challenge + fingerprint')
  awTrackAction('generate_click')
  const btn = document.getElementById('awCta')
  btn.className = 'aw-btn state-solving'
  btn.innerHTML = '<span class="aw-btn-spinner"></span> Verifying…'
  let pow, deviceFp
  try {
    const [chRes, fp] = await Promise.all([
      fetch(awFn('pow-challenge'), { headers: awAuth }),
      awDeviceFingerprint(),
    ])
    if (!chRes.ok) throw new Error('challenge request failed')
    const ch = await chRes.json()
    const nonce = await awSolvePow(ch.id, ch.difficulty, ch.expiresAt)
    pow = { ...ch, nonce }
    deviceFp = fp
  } catch (e) {
    awUpdateBtn()
    awToast('Verification failed. Please try again.', true)
    return
  }
  awUpdateBtn()
  awShowView('loading'); awSetStep(2)
  const form = new FormData()
  form.append('image', awFile)
  form.append('preset', awStyle)
  form.append('boat_count', awBoatCount)
  form.append('turnstile_token', awTurnstileToken)
  form.append('pow_id', pow.id)
  form.append('pow_difficulty', String(pow.difficulty))
  form.append('pow_expires_at', String(pow.expiresAt))
  form.append('pow_signature', pow.signature)
  form.append('pow_nonce', String(pow.nonce))
  if (deviceFp) form.append('device_fp', deviceFp)
  const carNameVal = document.getElementById('awCarName').value.trim()
  if (carNameVal) form.append('car_name', carNameVal)
  const labels = ['Analysing your vessel…','Generating artwork…','Adding style…','Almost done…']
  let li = 0
  const lt = setInterval(() => {
    document.getElementById('awLoadingLabel').textContent = labels[li % labels.length]; li++
  }, 6000)
  try {
    const res  = await fetch(awFn('generate-preview'), { method: 'POST', headers: awAuth, body: form })
    const data = await res.json()
    awResetTurnstile()
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
    awJobId = data.job_id
    const job = await awPollJob(awJobId)
    clearInterval(lt)
    if (job.status === 'failed') {
      throw new Error(job.error || 'Something went wrong. Please try again.')
    }
    awLastWatermarkedUrl = job.watermarked_url || null
    const retryThumb = document.getElementById('awRetryThumb')
    if (retryThumb) retryThumb.src = awLastWatermarkedUrl || ''
    awSetStep(2); awShowView('email')
  } catch (e) {
    clearInterval(lt); awResetTurnstile(); awShowView('upload'); awSetStep(1)
    awToast(e.message || 'Something went wrong. Please try again.', true)
  }
}

// generate-preview now responds the instant the job is queued instead of waiting
// for the OpenAI render — a single render can take well over a minute, which
// blows past the ~100s timeout most CDNs/reverse proxies enforce on one request.
// The actual generation runs server-side in the background; this polls poll-status
// (backed by the same jobs row) until it lands on a terminal state.
async function awPollJob(jobId) {
  const startedAt = Date.now()
  const maxWaitMs = 5 * 60 * 1000
  const pollIntervalMs = 2500
  while (Date.now() - startedAt < maxWaitMs) {
    const res = await fetch(`${awFn('poll-status')}?job_id=${encodeURIComponent(jobId)}`, { headers: awAuth })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
    if (data.status === 'awaiting_email' || data.status === 'done' || data.status === 'failed') return data
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
  }
  throw new Error('Taking longer than expected. Please try again in a moment.')
}

// Re-runs generation with the same uploaded photo, style, and boat count.
// The Turnstile widget only lives (and can only solve) inside the upload
// view, so this briefly switches back there, forces a fresh challenge, and
// lets awTurnstileCallback auto-fire awHandleCTA() once it resolves —
// usually near-instant for a real browser — instead of making the user
// manually re-click "Generate".
function awOpenLightbox() {
  if (!awLastWatermarkedUrl) return
  document.getElementById('awLightboxImg').src = awLastWatermarkedUrl
  document.getElementById('awLightbox').classList.add('aw-active')
}
function awCloseLightbox() {
  document.getElementById('awLightbox').classList.remove('aw-active')
}

function awHandleTryAgain() {
  awAutoRetry = true
  awShowView('upload'); awSetStep(1)
  awResetTurnstile()
}

async function awHandleEmail() {
  const email = document.getElementById('awEmail').value.trim()
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    awToast('Please enter a valid email address.', true); return
  }
  if (awIsDisposableEmail(email)) {
    awToast('Please use a real email address.', true); return
  }
  const btn = document.getElementById('awEmailBtn')
  btn.textContent = 'Sending…'; btn.className = 'aw-btn state-loading'; btn.onclick = null
  try {
    const res  = await fetch(awFn('collect-email'), {
      method: 'POST',
      headers: { ...awAuth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ job_id: awJobId, email }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
    awProductHandle = data.product_handle ?? null
    awSetStep(3); awShowView('done')
    if (awProductHandle && AW_SHOPIFY_STORE) {
      setTimeout(() => { window.location.href = `https://${location.hostname}/products/${awProductHandle}` }, 2000)
    } else {
      document.getElementById('awDoneMsg').textContent = 'Check your inbox for your portrait preview and next steps.'
    }
  } catch (e) {
    btn.textContent = 'Send me my portrait →'; btn.className = 'aw-btn state-ready'; btn.onclick = awHandleEmail
    awToast(e.message || 'Something went wrong. Please try again.', true)
  }
}

function awShowView(name) {
  ;['upload','loading','email','done'].forEach(v => {
    const id = 'awView' + v.charAt(0).toUpperCase() + v.slice(1)
    document.getElementById(id).classList.toggle('aw-hidden', v !== name)
  })
  if (name === 'upload') { awStartTurnstileWatchdog(); awUpdateBtn() }
  else clearTimeout(awTurnstileTimer)
}

function awSetStep(n) {
  ;[1,2,3].forEach(i => {
    const el = document.getElementById(`awStep${i}`)
    el.classList.toggle('active', i === n)
    el.classList.toggle('done', i < n)
  })
}

// Force step lines to render — inline styles beat any Shopify theme CSS
document.querySelectorAll('.aw-step-line').forEach(function(el) {
  el.setAttribute('style', [
    'flex: 1',
    'display: block',
    'height: 0',
    'min-width: 16px',
    'border: none',
    'border-top: 1px solid #c8d6e0',
    'margin: 0',
    'padding: 0',
    'background: none',
    'align-self: center',
    'visibility: visible',
    'opacity: 1',
  ].join(';'))
})

// ── PASTE (Ctrl+V) ─────────────────────────────────────────────
document.addEventListener('paste', (e) => {
  if (awFile) return
  const items = e.clipboardData?.items
  if (!items) return
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) { awProcessFile(file); break }
    }
  }
})

// ── RIGHT-CLICK CONTEXT MENU ────────────────────────────────────
const awCtxMenu = document.getElementById('awCtxMenu')

awZone.addEventListener('contextmenu', (e) => {
  if (awFile) return
  e.preventDefault()
  awCtxMenu.style.display = 'block'
  awCtxMenu.style.left = e.clientX + 'px'
  awCtxMenu.style.top  = e.clientY + 'px'
})

document.getElementById('awCtxPaste').addEventListener('click', async () => {
  awCtxMenu.style.display = 'none'
  try {
    const clipItems = await navigator.clipboard.read()
    for (const item of clipItems) {
      const imageType = item.types.find(t => t.startsWith('image/'))
      if (imageType) {
        const blob = await item.getType(imageType)
        const ext  = imageType.split('/')[1] || 'png'
        const file = new File([blob], `pasted.${ext}`, { type: imageType })
        awProcessFile(file)
        return
      }
    }
    awToast('No image found in clipboard.', true)
  } catch {
    awToast('Clipboard access denied. Try Ctrl+V instead.', true)
  }
})

document.addEventListener('click',       () => { awCtxMenu.style.display = 'none' })
document.addEventListener('contextmenu', (e) => {
  if (!e.target.closest('#awUploadZone')) awCtxMenu.style.display = 'none'
})

let awToastTimer
function awToast(msg, isError = false) {
  const t = document.getElementById('awToast')
  t.textContent = msg; t.classList.toggle('error', isError); t.classList.add('show')
  clearTimeout(awToastTimer)
  awToastTimer = setTimeout(() => t.classList.remove('show'), 4500)
}
